<script setup lang="ts">
/**
 * Client sales — el estado de cuenta del centro, sin período (§37).
 *
 * Es otra pregunta que la del dashboard, y por eso es otra pantalla: allá el eje
 * es el período y se cierra —enero es enero para siempre—; acá la pregunta es
 * "quién me debe", que no tiene período. Una deuda de hace ocho meses sigue
 * siendo una deuda, y esconderla porque cayó fuera del rango elegido es peor que
 * no tener el reporte.
 *
 * Una sola pregunta y una sola tabla. El corte por mes lo dibuja el dashboard, y
 * el ranking por servicio también (§38.1): tener acá una versión sin período de
 * lo mismo obligaba a comparar dos números parecidos calculados distinto, que es
 * peor que no tener el segundo.
 *
 * Desde §42 sale de las mismas dos fuentes que el dashboard: lo cobrado de los
 * recibos y lo pendiente tarifado con la lista de hoy. Antes usaba el precio
 * cotizado en la reserva, así que el saldo de acá, el del dashboard y el del
 * recibo eran tres números distintos para la misma deuda.
 */
import { getClientSalesReport, type ClientSalesReport } from '@/api/reports'
import AlertBox from '@/components/AlertBox.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage } from '@/composables/useSnackbar'

definePage({ meta: { requiresAuth: true } })

const router = useRouter()
const { currentId } = useCurrentDiveCenter()
const { formatCurrency } = usePreferences()

const report = ref<ClientSalesReport | null>(null)
const loading = ref(false)
const error = ref('')

/**
 * Qué clientes mostrar.
 *
 * `pending` es el default de la pantalla: se abre para reclamar, no para
 * repasar. Los que no deben nada siguen a un click, porque la otra pregunta
 * —cuánto gastó este cliente en total— también se hace acá.
 */
const filter = ref<'pending' | 'paid' | 'all'>('pending')

const search = ref('')

/*
 * 🪤 El filtro, la búsqueda y la página son del SERVIDOR.
 *
 * Los totales se calculan sobre toda la cartera antes de recortar, así que
 * "cuánto me deben" no depende de qué filtro esté puesto ni de en qué página se
 * esté parado. Lo que se pagina es cuánto viaja y cuánto pinta el navegador.
 */
const page = ref(1)
const itemsPerPage = ref(10)

const load = async () => {
  if (!currentId.value)
    return

  loading.value = true
  error.value = ''
  try {
    report.value = await getClientSalesReport(currentId.value, {
      filter: filter.value,
      search: search.value.trim() || undefined,
      limit: itemsPerPage.value,
      skip: (page.value - 1) * itemsPerPage.value,
    })
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load the client report.')
    report.value = null
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/*
 * Cambiar de centro recarga: es una pantalla de la CUENTA, así que el `:key` del
 * `RouterView` no la remonta (§23.3).
 */
watch(currentId, () => {
  page.value = 1
  load()
})

// Cambiar el filtro o el tamaño de página vuelve a la primera: la página cuatro
// de otra lista no quiere decir nada.
watch([filter, itemsPerPage], () => {
  page.value = 1
  load()
})

watch(page, load)

/*
 * El buscador va con debounce: cada tecla dispararía una consulta, y la
 * respuesta lenta de una vieja podría pisar a la nueva. 350 ms es el punto donde
 * deja de sentirse el retraso y ya no se manda una por letra — el mismo criterio
 * que el listado de clientes.
 */
let searchTimer: ReturnType<typeof setTimeout> | undefined

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    page.value = 1
    load()
  }, 350)
})

const money = (amount: number) => formatCurrency(amount, report.value?.currency)

const clients = computed(() => report.value?.byClient ?? [])

/** Lo que suman los clientes del filtro, no solo los de esta página. */
const shownPending = computed(() => report.value?.shownPending ?? 0)

const clientHeaders = [
  { title: 'Client', key: 'clientName' },
  { title: 'Bookings', key: 'bookings', align: 'end' as const },
  { title: 'Total', key: 'total', align: 'end' as const },
  { title: 'Paid', key: 'paid', align: 'end' as const },
  { title: 'Pending', key: 'pending', align: 'end' as const },
  { title: '', key: 'actions', sortable: false, align: 'end' as const, width: 150 },
]

/**
 * Del saldo al recibo, en un click.
 *
 * Es el camino más corto para cobrar: esta pantalla es donde se ve quién debe, y
 * obligar a ir a Receipts y volver a buscar al mismo cliente sería hacer dos
 * veces el trabajo de encontrarlo. La ruta `/new` arma el borrador con todo lo
 * pendiente, así que no hace falta pasarle nada más que el cliente.
 *
 * Sin ficha no hay botón: la persona se borró y solo queda su nombre congelado,
 * y un recibo necesita a quién emitírselo.
 */
const openReceipt = (clientId?: string) => {
  if (!clientId) return

  router.push(`/sales/receipts/new?clientId=${clientId}`)
}

</script>

<template>
  <div>
    <AlertBox
      variant="danger"
      :message="error"
    />

    <VCard>
          <VCardText class="d-flex flex-wrap align-center ga-4">
            <VChipGroup
              v-model="filter"
              mandatory
              selected-class="text-primary"
            >
              <VChip value="pending">
                With a balance
              </VChip>
              <VChip value="paid">
                Settled
              </VChip>
              <VChip value="all">
                All
              </VChip>
            </VChipGroup>

            <VSpacer />

            <VTextField
              v-model="search"
              placeholder="Search client"
              prepend-inner-icon="ri-search-line"
              density="compact"
              clearable
              hide-details
              style="min-inline-size: 200px; max-inline-size: 280px;"
            />
          </VCardText>

          <VDivider />

          <!-- El total de lo que se está mirando, no el del centro: con el
               filtro puesto en "con saldo" es exactamente lo que hay por cobrar. -->
          <VCardText
            v-if="report"
            class="d-flex justify-end text-body-2"
          >
            <span class="text-medium-emphasis me-2">Outstanding shown:</span>
            <span class="font-weight-medium">{{ money(shownPending) }}</span>
          </VCardText>

          <!--
            La tabla del servidor: el filtro, la búsqueda y la página se resuelven
            allá, así que `items-length` es el total del backend y no el largo de
            esta página. `@update:options` se ignora porque los modelos ya
            disparan la recarga.
          -->
          <VDataTableServer
            v-model:page="page"
            v-model:items-per-page="itemsPerPage"
            :items="clients"
            :items-length="report?.clientTotal ?? 0"
            :headers="clientHeaders"
            :loading="loading"
            :items-per-page-options="[10, 25, 50]"
            item-value="clientName"
            class="text-no-wrap"
            @update:options="() => {}"
          >
            <template #item.total="{ item }">
              {{ money(item.total) }}
            </template>

            <template #item.paid="{ item }">
              <span class="text-success">{{ money(item.paid) }}</span>
            </template>

            <template #item.pending="{ item }">
              <span :class="item.pending > 0 ? 'text-warning font-weight-medium' : 'text-medium-emphasis'">
                {{ money(item.pending) }}
              </span>
            </template>

            <template #item.actions="{ item }">
              <VBtn
                v-if="item.pending > 0 && item.clientId"
                size="small"
                variant="tonal"
                prepend-icon="ri-receipt-line"
                @click="openReceipt(item.clientId)"
              >
                Receipt
              </VBtn>
            </template>

            <template #no-data>
              <div class="text-center text-medium-emphasis py-8">
                No clients match this filter.
              </div>
            </template>
          </VDataTableServer>
  </VCard>
  </div>
</template>
